import React, { useMemo, useRef, useState } from 'react';
import {
  FiCheck,
  FiGlobe,
  FiImage,
  FiLoader,
  FiMove,
  FiSave,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { getDirectMediaUrlInputValue } from '../utils/mediaUrl';
import { resolveServiceMediaUrl } from '../utils/serviceContent';
import AdminTranslationPopoverPortal from './AdminTranslationPopoverPortal';
import './AdminProductImagesTranslation.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];

const getTranslatedWork = (translation, workIndex, workId) => {
  const works = Array.isArray(translation?.data?.works) ? translation.data.works : [];
  if (workId) {
    const byId = works.find((item) => String(item?.id || '') === String(workId));
    if (byId) return byId;
    const indexed = works[workIndex];
    return indexed && !indexed.id ? indexed : null;
  }
  return works[workIndex] || null;
};

const imageFromTranslation = (translation, workIndex, workId) => (
  String(getTranslatedWork(translation, workIndex, workId)?.image || '').trim()
);

const emitSaveState = (serviceId, locale, fieldPath, state) => {
  window.dispatchEvent(new CustomEvent('haryad:translation-save-state', {
    detail: {
      entityType: 'service',
      entityId: String(serviceId),
      locale,
      fieldPath,
      state,
    },
  }));
};

const AdminServiceWorkImageTranslation = ({
  serviceId,
  workIndex,
  workId,
  workTitle,
  sourceImage = '',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locales, setLocales] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [image, setImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);

  const fieldPath = `works.${workIndex}.image`;
  const enabledLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeLanguage = enabledLocales.find((locale) => locale.code === activeLocale);
  const activeTranslation = translations.find((item) => item.locale === activeLocale);

  const selectLocale = (localeCode, items = translations) => {
    setActiveLocale(localeCode);
    setImage(imageFromTranslation(items.find((item) => item.locale === localeCode), workIndex, workId));
    setError('');
  };

  const load = async () => {
    const [localeResponse, translationResponse] = await Promise.all([
      api.get('/api/locales/admin'),
      api.get(`/api/services/${serviceId}/translations`),
    ]);
    const localeItems = Array.isArray(localeResponse.data) ? localeResponse.data : [];
    const translationItems = Array.isArray(translationResponse.data) ? translationResponse.data : [];
    setLocales(localeItems);
    setTranslations(translationItems);
    const firstLocale = localeItems.find((item) => item.enabled !== false && item.code !== 'fa')?.code || '';
    selectLocale(firstLocale, translationItems);
  };

  const handleOpen = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setLoading(true);
    setError('');
    try {
      await load();
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'دریافت تصاویر زبان‌ها انجام نشد');
    } finally {
      setLoading(false);
    }
  };

  const updateLocalTranslation = (localeCode, nextImage) => {
    setTranslations((items) => {
      const existing = items.find((item) => item.locale === localeCode);
      const next = JSON.parse(JSON.stringify(existing || {
        locale: localeCode,
        status: 'draft',
        data: {},
      }));
      if (!next.data) next.data = {};
      if (!Array.isArray(next.data.works)) next.data.works = [];
      const matchingIndex = workId
        ? next.data.works.findIndex((item) => String(item?.id || '') === String(workId))
        : -1;
      const targetIndex = matchingIndex >= 0 ? matchingIndex : workIndex;
      const current = next.data.works[targetIndex];
      next.data.works[targetIndex] = current && (!workId || !current.id || String(current.id) === String(workId))
        ? { ...current, ...(workId && !current.id ? { id: workId } : {}), image: nextImage }
        : { ...(workId ? { id: workId } : {}), image: nextImage };
      next.hasUnpublishedChanges = true;
      return existing
        ? items.map((item) => (item === existing ? next : item))
        : [...items, next];
    });
  };

  const saveImage = async (value) => {
    if (!activeLocale) return false;
    const normalizedValue = String(value || '').trim();
    setSaving(true);
    emitSaveState(serviceId, activeLocale, fieldPath, 'saving');
    setError('');
    try {
      await api.patch(`/api/services/${serviceId}/translations/${activeLocale}/field`, {
        path: fieldPath,
        value: normalizedValue,
      });
      setImage(normalizedValue);
      updateLocalTranslation(activeLocale, normalizedValue);
      emitSaveState(serviceId, activeLocale, fieldPath, 'saved');
      window.dispatchEvent(new CustomEvent('haryad:translations-changed', {
        detail: { entityType: 'service', entityId: serviceId },
      }));
      return true;
    } catch (saveError) {
      emitSaveState(serviceId, activeLocale, fieldPath, 'error');
      setError(saveError.data?.message || saveError.message || 'ذخیره تصویر انجام نشد');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setError('حجم تصویر نباید بیشتر از ۵ مگابایت باشد.');
      return;
    }

    setUploading(true);
    emitSaveState(serviceId, activeLocale, `upload-${fieldPath}`, 'saving');
    setError('');
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.upload('/api/services/upload-image', formData);
      const imageUrl = response.data?.imageUrl;
      if (!response.success || !imageUrl) throw new Error('آپلود تصویر انجام نشد');
      const saved = await saveImage(imageUrl);
      if (!saved) throw new Error('ذخیره تصویر زبان انجام نشد');
      emitSaveState(serviceId, activeLocale, `upload-${fieldPath}`, 'saved');
    } catch (uploadError) {
      emitSaveState(serviceId, activeLocale, `upload-${fieldPath}`, 'error');
      setError(uploadError.data?.message || uploadError.message || 'آپلود تصویر انجام نشد');
    } finally {
      setUploading(false);
    }
  };

  if (!serviceId) return null;

  return (
    <span className="admin-product-images-translation" ref={rootRef}>
      <button
        type="button"
        className={`admin-product-images-translation__trigger ${open ? 'is-active' : ''}`}
        onClick={handleOpen}
        title="تصویر نمونه‌کار در زبان‌های دیگر"
        aria-label={`مدیریت تصویر چندزبانه ${workTitle || `نمونه‌کار ${workIndex + 1}`}`}
        aria-expanded={open}
      >
        <FiGlobe aria-hidden="true" />
        <FiImage aria-hidden="true" />
        <small>{enabledLocales.length || ''}</small>
      </button>

      {open && (
        <AdminTranslationPopoverPortal
          anchorRef={rootRef}
          className="admin-product-images-translation__popover"
          ariaLabel="تصویر نمونه‌کار برای زبان‌های دیگر"
          onClose={() => setOpen(false)}
        >
          {({ dragHandleProps }) => <>
            <header {...dragHandleProps}>
              <div>
                <strong>تصویر نمونه‌کار برای زبان‌های دیگر</strong>
                <small>برای کوردی و انگلیسی می‌توانید تصویر مستقل آپلود کنید؛ تصویر فارسی به این زبان‌ها منتقل نمی‌شود.</small>
                <small className="admin-translation-popover__drag-hint"><FiMove aria-hidden="true" /> برای جابه‌جایی بکشید</small>
              </div>
              <button type="button" className="ui-close-button" onClick={() => setOpen(false)} aria-label="بستن"><FiX /></button>
            </header>

            {loading ? (
              <div className="admin-product-images-translation__loading"><FiLoader /> در حال دریافت...</div>
            ) : (
              <>
                <div className="admin-product-images-translation__tabs" role="tablist" aria-label="انتخاب زبان تصویر نمونه‌کار">
                  {enabledLocales.map((locale) => {
                    const translation = translations.find((item) => item.locale === locale.code);
                    const filled = Boolean(imageFromTranslation(translation, workIndex, workId));
                    return (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeLocale === locale.code}
                        className={activeLocale === locale.code ? 'is-active' : ''}
                        key={locale.code}
                        onClick={() => selectLocale(locale.code)}
                      >
                        <b>{locale.code.toUpperCase()}</b>
                        {locale.nativeName}
                        <small>{filled ? '۱/۱' : '۰/۱'}</small>
                        {filled ? <FiCheck /> : null}
                      </button>
                    );
                  })}
                </div>

                {activeLanguage && (
                  <div className="admin-product-images-translation__content">
                    <div className="admin-product-images-translation__source-note">
                      <FiImage /> تصویر فارسی فقط در نسخهٔ فارسی نمایش داده می‌شود.
                    </div>

                    <div className="admin-product-images-translation__grid" style={{ gridTemplateColumns: '1fr' }} dir={activeLanguage.direction || 'rtl'}>
                      <section>
                        <div className="admin-product-images-translation__field-head" dir="rtl">
                          <span><strong>{workTitle ? `تصویر «${workTitle}»` : 'تصویر نمونه‌کار'}</strong><small>مستقل برای این زبان</small></span>
                          {sourceImage ? <a href={resolveServiceMediaUrl(sourceImage)} target="_blank" rel="noreferrer">مشاهده فارسی</a> : null}
                        </div>

                        <input
                          id={`localized-service-work-${serviceId}-${workIndex}-${activeLocale}`}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                          onChange={uploadImage}
                          disabled={uploading || saving}
                          hidden
                        />
                        <label
                          className={`admin-product-images-translation__upload ${uploading || saving ? 'is-busy' : ''}`}
                          htmlFor={`localized-service-work-${serviceId}-${workIndex}-${activeLocale}`}
                          dir="rtl"
                        >
                          {uploading || saving ? <FiLoader /> : <FiUploadCloud />}
                          <span>
                            <strong>{uploading || saving ? 'در حال ذخیره تصویر...' : 'آپلود تصویر این زبان'}</strong>
                            <small>تا ۵ مگابایت</small>
                          </span>
                        </label>

                        <div className="admin-product-images-translation__url" dir="rtl">
                          <input
                            type="url"
                            dir="ltr"
                            value={getDirectMediaUrlInputValue(image)}
                            placeholder={image.startsWith('/uploads/') ? 'برای جایگزینی، آدرس مستقیم وارد کنید' : 'https://example.com/project.webp'}
                            onChange={(event) => setImage(event.target.value)}
                          />
                          <button type="button" onClick={() => saveImage(image)} disabled={uploading || saving}>
                            <FiSave /> ذخیره آدرس
                          </button>
                        </div>

                        {image ? (
                          <div className="admin-product-images-translation__preview is-banner">
                            <img src={resolveServiceMediaUrl(image)} alt={`پیش‌نمایش تصویر ${workTitle || 'نمونه‌کار'} به زبان ${activeLanguage.nativeName}`} />
                            <button type="button" onClick={() => saveImage('')} disabled={uploading || saving} dir="rtl">
                              <FiTrash2 /> حذف
                            </button>
                          </div>
                        ) : (
                          <div className="admin-product-images-translation__empty is-banner" dir="rtl">
                            برای این زبان تصویری ثبت نشده است.
                          </div>
                        )}
                      </section>
                    </div>

                    <footer dir="rtl">
                      <div>
                        <small>
                          {activeTranslation?.hasUnpublishedChanges
                            ? 'تصویر در پیش‌نویس ذخیره شده است؛ برای نمایش در سایت، ترجمه این زبان را از پایین فرم دوباره منتشر کنید.'
                            : activeTranslation?.status === 'published'
                              ? 'نسخه منتشرشده این زبان به‌روز است.'
                              : 'پس از تکمیل ترجمه، از بخش انتشار ترجمهٔ خدمات استفاده کنید.'}
                        </small>
                        {error && <span>{error}</span>}
                      </div>
                    </footer>
                  </div>
                )}

                {!enabledLocales.length && !error && (
                  <div className="admin-product-images-translation__empty-languages">زبان دیگری فعال نشده است.</div>
                )}
              </>
            )}

            {error && !activeLanguage && <div className="admin-product-images-translation__error">{error}</div>}
          </>}
        </AdminTranslationPopoverPortal>
      )}
    </span>
  );
};

export default AdminServiceWorkImageTranslation;
